/**
 * ContentRunwayIndicator — "Queue" panel.
 *
 * Side-by-side layout, centered horizontally under the puzzle list:
 *   Left:  "QUEUE" heading + colored circle (same height as calendar SVG)
 *          with number + "DAYS" (PhraseMaker) inside
 *   Right: "NEXT VACANCY" heading + mini calendar SVG highlighting the first gap date
 *
 * Circle color coding:
 *   15+: green (#387c47)
 *   8-14: gold (#d1930b)
 *   3-7: hint-button orange (#d26f28)
 *   0-2: loser red (#b84927)
 *
 * For values >99: show "99" inside circle + smaller "+" outside upper-right.
 */

import React, { useMemo } from 'react';
import Phrase from '../../utils/phraseMaker';
import VacancyCalendar, { CALENDAR_SVG_H } from './VacancyCalendar';


function getRunwayColor(days: number): string {
  if (days >= 15) return '#387c47';
  if (days >= 8) return '#d1930b';
  if (days >= 3) return '#d26f28';
  return '#b84927';
}

export function computeRunway(scheduledDates: Set<string>, today: string): number {
  if (!scheduledDates.has(today)) return 0;
  const todayDate = new Date(today + 'T00:00:00');
  let count = 0;
  for (let i = 1; i <= 999; i++) {
    const d = new Date(todayDate);
    d.setDate(d.getDate() + i);
    const iso = d.toISOString().slice(0, 10);
    if (!scheduledDates.has(iso)) break;
    count = i;
  }
  return count;
}

export function findNextUnscheduled(scheduledDates: Set<string>, today: string): string {
  const todayDate = new Date(today + 'T00:00:00');
  for (let i = 0; i <= 999; i++) {
    const d = new Date(todayDate);
    d.setDate(d.getDate() + i);
    const iso = d.toISOString().slice(0, 10);
    if (!scheduledDates.has(iso)) return iso;
  }
  return today; // fallback
}

// ─── Circle: 1.2× calendar height, then scaled up 25% ───
const CIRCLE_SIZE = Math.round(CALENDAR_SVG_H * 1.5);     // ~143 (was 1.2 → 1.5 = 1.2×1.25)
const NUMBER_FONT = Math.round(CALENDAR_SVG_H * 0.6);     // ~57  (was 0.48 → 0.6 = 0.48×1.25)
const DAYS_FONT = Math.round(NUMBER_FONT * 0.45);         // ~26  (proportional)

// ─── Sub-label style (one step below regionLabel) ───
const HEADING_STYLE: React.CSSProperties = {
  fontFamily: 'Inter, sans-serif',
  fontSize: 10,
  fontWeight: 600,
  color: '#f7e5c5',
  opacity: 0.6,
  textTransform: 'uppercase',
  letterSpacing: 1,
  textAlign: 'center',
  marginBottom: 8,
};

interface ContentRunwayIndicatorProps {
  scheduledDates: Set<string> | null;
}

const ContentRunwayIndicator: React.FC<ContentRunwayIndicatorProps> = ({ scheduledDates }) => {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const runway = scheduledDates !== null ? computeRunway(scheduledDates, today) : null;
  const nextVacancy = scheduledDates !== null ? findNextUnscheduled(scheduledDates, today) : null;

  if (runway === null) return null;

  const color = getRunwayColor(runway);
  const isOverflow = runway > 99;
  const displayNum = '58'; // TEMP: hardcoded for visual assessment

  // The taller element determines the label row position.
  // Circle is CIRCLE_SIZE tall; calendar is CALENDAR_SVG_H tall.
  const maxH = Math.max(CIRCLE_SIZE, CALENDAR_SVG_H);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      paddingRight: 10,
    }}>
      {/* Widget row — shifted 10px left via paddingRight on parent */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-evenly',
        width: '100%',
      }}>
        {/* ─── Left: Queue circle ─── */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
          <div style={{
            position: 'relative',
            width: CIRCLE_SIZE,
            height: CIRCLE_SIZE,
            flexShrink: 0,
          }}>
            <svg
              width={CIRCLE_SIZE}
              height={CIRCLE_SIZE}
              viewBox={`0 0 ${CIRCLE_SIZE} ${CIRCLE_SIZE}`}
              style={{ position: 'absolute', top: 0, left: 0 }}
            >
              <circle
                cx={CIRCLE_SIZE / 2}
                cy={CIRCLE_SIZE / 2}
                r={Math.round((CIRCLE_SIZE / 2 - 2) * 0.8)}
                fill={color}
                stroke="#262626"
                strokeWidth={3}
              />
            </svg>

            {/* Number + DAYS vertically centered in circle */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: CIRCLE_SIZE,
              height: CIRCLE_SIZE,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              transform: 'translateY(-4px)',
            }}>
              <Phrase
                text={displayNum}
                fontSize={NUMBER_FONT}
                textAnchor="middle"
                style={{ display: 'block', marginBottom: -9, marginRight: 4 }}
              />
              <Phrase
                text="DAYS"
                fontSize={DAYS_FONT}
                textAnchor="middle"
                style={{ display: 'block', marginTop: -13, marginLeft: 6 }}
              />
            </div>

            {/* Overflow "+" outside circle, upper-right */}
            {isOverflow && (
              <div style={{
                position: 'absolute',
                top: -2,
                right: -14,
              }}>
                <Phrase
                  text="+"
                  fontSize={Math.round(NUMBER_FONT * 0.5)}
                />
              </div>
            )}
          </div>
        </div>

        {/* ─── Right: Next Vacancy calendar ─── */}
        {nextVacancy && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
            <VacancyCalendar vacancyDate={nextVacancy} />
          </div>
        )}
      </div>

      {/* Label row — aligned horizontally, slightly below calendar bottom */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-evenly',
        marginTop: maxH > CIRCLE_SIZE ? 10 : 8,
      }}>
        <span style={{ ...HEADING_STYLE, marginBottom: 0, marginTop: 0, width: CIRCLE_SIZE, textAlign: 'center' }}>Queue</span>
        {nextVacancy && (
          <span style={{ ...HEADING_STYLE, marginBottom: 0, marginTop: 0, width: CIRCLE_SIZE, textAlign: 'center' }}>Next Vacancy</span>
        )}
      </div>
    </div>
  );
};

export default React.memo(ContentRunwayIndicator);
